'use client'
import type { FC, Dispatch, SetStateAction } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'next/navigation'
// import NextImage from 'next/image'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import type { TypeLang } from '@/schema'
import type { TypeOriginsObj } from '@/app/[lang]/(public)/(staticPages)/about-us/origins/page'
import { designGrid, hideScrollbar, mq, presetsFluid, vw } from '@/styles'
import { AnimatedNumber, Parallax } from './base'
import { Format } from '@number-flow/react'
import { Medium } from '@/components'

type Props = {
  data: TypeOriginsObj[]
}

type YearProps = {
  data: TypeOriginsObj
  setCurrentYear?: Dispatch<SetStateAction<number | null>>
}

const YearArticle:FC<YearProps> = ({ data, setCurrentYear }) => {
  const { year, title, description, media } = data
  const { lang } = useParams() as { lang: TypeLang }
  const ref = useRef<HTMLElement>(null)
  const isInView = useInView(ref, {
    once: false,
    initial: false,
    // margin: '150px 0px 220px 0px'
    amount: .52
  })

  useEffect(() => {
    if (!isInView) return
    setCurrentYear?.(year)
  }, [isInView, setCurrentYear, year])

  return (
    <Article ref={ref} className={`layout-type--${data.layoutType}`}>
      <h3>{title}</h3>
      <p>{description}</p>
      {media.map((image, imgIndex) => (
        <div key={imgIndex} className={`absolute-wrapper wrapper--${imgIndex + 1}`}>
          <div className='media-wrapper'>
            <Parallax distance={(imgIndex + 1) * 15 }>
              <Medium className={`img-${imgIndex + 1} img-${year}`} data={image} parseImage={{ preferredLang: lang }}/>
            </Parallax>
          </div>
        </div>
      ))}
    </Article>
  )
}

const yearFormat:Format = {
  notation: 'standard',
  compactDisplay: 'long',
  trailingZeroDisplay: 'auto',
  useGrouping: false // This props removes the comma in thousands
} as const

export const MainOrigins:FC<Props> = ({ data }) => {
  const [currentYear, setCurrentYear] = useState<number | null>(data[0]?.year || null)

  return (
    <Main>
      <section>
				<hgroup>
					<div>	
						<h2>
							<span>
								<AnimatedNumber value={currentYear || 0} format={yearFormat} />
							</span>
						</h2>
					</div>
				</hgroup>
        {data.map((item, idx) => <YearArticle key={idx} data={item} setCurrentYear={setCurrentYear} />)}
      </section>
    </Main>
  )
}

const Main = styled.main`
  background-color: var(--color-background--dark);
  display: flex;
  grid-column: 1;
  scroll-snap-type: y mandatory;

  section {
    ${designGrid({})}
    color: var(--color-text--dark);
    position: relative;
    width: var(--viewport-width);
    width: var(--viewport-width--dynamic);
  }

	hgroup {
		bottom: 0;
		height: 100%;
		left: 0;
		position: absolute;
		right: 0;
		top: 0;
		width: 100%;

		> div {
			align-items: center;
			display: flex;
			height: 100vh;
			justify-content: center;
			position: sticky;
			top: 0;
		}
	}

  h2 {
    ${presetsFluid.font150_450()}
		${hideScrollbar()}
    letter-spacing: -10px; // Cannot put %
    grid-column: 1 / span 6;
    max-width: 100vw;
    max-width: 100dvw;
    margin-bottom: 0;
    opacity: .2;
    overflow-x: hidden;
    pointer-events: none;
    z-index: 1;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }
  }
`

const Article = styled(motion.article)`
  display: flex;
  flex-direction: column;
  grid-column: 1 / span 6;
  height: 100vh;
  margin: 100px 0;
  overflow: hidden;
  padding: 30px 0;
  padding: ${vw(20, 'mobile')} 0;
  position: relative;
  width: 100%;
  z-index: 2;
  ${hideScrollbar()}

  ${mq.greaterThan('tablet')} {
    height: calc(100vh + 130px);
    grid-column: 1 / span 12;
    margin: 0;
    padding: calc(100px + ${vw(30, 'desktop')}) 0;
  }

  ${mq.greaterThan('desktop')} {
    padding: 130px 0;
  }

  h3 {
    ${presetsFluid.font25_30()}
    margin-top: ${vw(10, 'mobile')};
    text-align: center;
    width: 100%;
    z-index: 3;

    ${mq.greaterThan('nexus7')} {
      margin-top: ${vw(10, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-top: 0;
    }

		${mq.greaterThan('desktop')} {
			${presetsFluid.font30_rel()}
		}
  }



  p {
    ${presetsFluid.font15()}
    margin-left: auto;
    margin-top: auto;
    max-width: ${vw(284, 'mobile')};
    opacity: .7;
    text-align: left;
    z-index: 3;

    ${mq.greaterThan('tablet')} {
      max-width: ${vw(284, 'desktop')};
    }

		${mq.greaterThan('desktop')} {
			${presetsFluid.font15_rel()}
		}
  }

  picture {
    display: block;
  }

  img {
    height: 100%;
    object-fit: contain;
    width: 100%;
  }

  .absolute-wrapper {
    position: absolute;

    > .media-wrapper {
      position: relative;
    }
  }

  .wrapper--1 > div,
  .img-1 {
    height: ${vw(154.5, 'mobile')};
    width: ${vw(233.5, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(154.5, 'tablet')};
      width: ${vw(233.5, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(309, 'desktop')};
      width: ${vw(467, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 309px;
      width: 467px;
    }
  }

  .wrapper--2 > div,
  .img-2 {
    height: ${vw(140, 'mobile')};
    width: ${vw(110, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(140, 'tablet')};
      width: ${vw(110, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(229, 'desktop')};
      width: ${vw(180, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 229px;
      width: 180px;
    }
  }

  .wrapper--3 > div,
  .img-3 {
    height: ${vw(125, 'mobile')};
    width: ${vw(134, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      height: ${vw(125, 'tablet')};
      width: ${vw(134, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(186, 'desktop')};
      width: ${vw(199, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 186px;
      width: 199px;
    }
  }

  &.layout-type--1 {
    .wrapper--1 {
      left: ${vw(7, 'mobile')};
      top: calc(100px + ${vw(92, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        left: ${vw(7, 'tablet')};
        top: calc(100px + ${vw(92, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        left: 0;
        top: calc(100px + ${vw(30, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        top: 130px;
      }
    }

    .wrapper--2 {
      right: ${vw(5, 'mobile')};
      top: calc(100px + ${vw(207, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        right: ${vw(5, 'tablet')};
        top: calc(100px + ${vw(207, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        right: 0;
        top: calc(100px + ${vw(30, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        top: 130px;
      }
    }

    .wrapper--3 {
      left: ${vw(7, 'mobile')};
      bottom: calc(100px + ${vw(153, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        left: ${vw(7, 'tablet')};
        bottom: calc(100px + ${vw(153, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        left: ${vw(49, 'desktop')};
        bottom: calc(100px + ${vw(34, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        bottom: 134px;
      }
    }
  }

  &.layout-type--2 {
    .wrapper--1 {
      left: ${vw(68, 'mobile')};
      bottom: calc(100px + ${vw(189.5, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        left: ${vw(68, 'tablet')};
        bottom: calc(100px + ${vw(189.5, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        left: ${vw(416, 'desktop')};
        bottom: calc(100px + ${vw(123, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        bottom: 223px;
      }
    }

    .wrapper--2 {
      left: ${vw(7, 'mobile')};
      top: calc(100px + ${vw(257, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        left: ${vw(7, 'tablet')};
        top: calc(100px + ${vw(257, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        left: 0;
        top: calc(100px + ${vw(213, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        top: 313px;
      }
    }

    .wrapper--3 {
      right: ${vw(8, 'mobile')};
      top: calc(100px + ${vw(92, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        right: ${vw(8, 'tablet')};
        top: calc(100px + ${vw(92, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        right: 0;
        top: calc(100px + ${vw(30, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        top: 130px;
      }
    }
  }

  &.layout-type--3 {
    .wrapper--1 {
      left: ${vw(7, 'mobile')};
      bottom: calc(100px + ${vw(157.5, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        left: ${vw(7, 'tablet')};
        bottom: calc(100px + ${vw(157.5, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        left: 0;
        bottom: calc(100px + ${vw(30, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        bottom: 130px;
      }
    }

    .wrapper--2 {
      left: ${vw(7, 'mobile')};
      top: calc(100px + ${vw(92, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        left: ${vw(7, 'tablet')};
        top: calc(100px + ${vw(92, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        left: ${vw(211, 'desktop')};
        top: calc(100px + ${vw(10, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        top: 110px;
      }
    }

    .wrapper--3 {
      right: ${vw(8, 'mobile')};
      top: calc(100px + ${vw(277, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        right: ${vw(8, 'tablet')};
        top: calc(100px + ${vw(277, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        right: 0;
        top: calc(100px + ${vw(300, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        top: 400px;
      }
    }
  }

  &.layout-type--4 {
    .wrapper--1 {
      right: ${vw(7.5, 'mobile')};
      top: calc(100px + ${vw(92, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        right: ${vw(7.5, 'tablet')};
        top: calc(100px + ${vw(92, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        right: 0;
        top: calc(100px + ${vw(30, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        top: 130px;
      }
    }

    .wrapper--2 {
      left: ${vw(7, 'mobile')};
      top: calc(100px + ${vw(196, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        left: ${vw(7, 'tablet')};
        top: calc(100px + ${vw(196, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        left: ${vw(76, 'desktop')};
        top: calc(100px + ${vw(30, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        top: 130px;
      }
    }

    .wrapper--3 {
      left: ${vw(86, 'mobile')};
      bottom: calc(100px + ${vw(181, 'mobile')});

      ${mq.greaterThan('nexus7')} {
        left: ${vw(86, 'tablet')};
        bottom: calc(100px + ${vw(181, 'tablet')});
      }

      ${mq.greaterThan('tablet')} {
        left: ${vw(239, 'desktop')};
        bottom: calc(100px + ${vw(34, 'desktop')});
      }

      ${mq.greaterThan('desktop')} {
        bottom: 134px;
      }
    }
  }
`
